import { useMemo, useRef, useEffect } from "react";
import { Box, SxProps, Theme, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { IVideo } from "src/types/common";

interface IHeroSliderSlideVideoProps {
    desktopVideo: IVideo;
    desktopSmallVideo?: IVideo;
    mobVideo?: IVideo;
    tabVideo?: IVideo;
    onVideoLoad?: () => void;
    onVideoError?: () => void;
    onVideoSourceChange?: () => void;
    sx?: SxProps<Theme>;
    isPaused?: boolean;
}

export const HeroSliderSlideVideo = ({
    desktopVideo,
    desktopSmallVideo,
    mobVideo,
    tabVideo,
    onVideoLoad,
    onVideoError,
    onVideoSourceChange,
    sx,
    isPaused = false,
}: IHeroSliderSlideVideoProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktopSmall = useMediaQuery(theme.breakpoints.between("md", "lg"));
    const previousVideoSrc = useRef<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const currentVideoSrc = useMemo(() => {
        if (isMobile && mobVideo) {
            return mobVideo.url;
        }
        if (isTablet && tabVideo) {
            return tabVideo.url;
        }
        if (isDesktopSmall && desktopSmallVideo) {
            return desktopSmallVideo.url;
        }

        return desktopVideo.url;
    }, [isMobile, isTablet, isDesktopSmall, mobVideo, tabVideo, desktopSmallVideo, desktopVideo]);

    const handleVideoLoadStart = () => {
        if (previousVideoSrc.current !== null && previousVideoSrc.current !== currentVideoSrc) {
            onVideoSourceChange?.();
        }
        previousVideoSrc.current = currentVideoSrc || null;
    };

    const handleVideoLoad = () => {
        onVideoLoad?.();
    };

    const handleVideoError = () => {
        onVideoError?.();
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        if (isPaused) {
            video.pause();
        } else {
            video.currentTime = 0;
            video.play();
        }
    }, [isPaused]);

    return (
        <Box
            component="video"
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            onLoadStart={handleVideoLoadStart}
            onCanPlay={handleVideoLoad}
            onError={handleVideoError}
            src={currentVideoSrc}
            key={currentVideoSrc}
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "right center",
                zIndex: 1,
                ...sx,
            }}
        />
    );
};
